import { Link } from "react-router-dom";
import { Users, DollarSign, BarChart3, FileText, Unlock, Server, Database, TrendingUp, Lock, Wrench, MessageCircle } from "lucide-react";
import "./HowItWorks.css";

export const HowItWorks = () => {
  return (
    <main className="how-it-works">
      <div className="hero-section">
        <h1>How It Works</h1>
        <p className="hero-subtitle">
          Automated campaign finance data extraction with 99.94% accuracy
        </p>
      </div>

      {/* What We Extract */}
      <section className="content-section">
        <h2>What We Extract</h2>
        <p className="section-intro">
          Our platform automatically extracts comprehensive data from Missouri Ethics Commission (MEC) PDF reports.
        </p>

        <div className="feature-grid">
          <div className="feature-card">
            <div className="feature-icon"><Users size={32} /></div>
            <h3>Donor Information</h3>
            <ul>
              <li>Donor name & address</li>
              <li>Employer information</li>
              <li>Contribution date & amount</li>
              <li>Contribution type (monetary/in-kind)</li>
              <li>City & state data</li>
            </ul>
          </div>

          <div className="feature-card">
            <div className="feature-icon"><DollarSign size={32} /></div>
            <h3>Expense Data</h3>
            <ul>
              <li>Payee name</li>
              <li>Expense amount & date</li>
              <li>Purpose of expenditure</li>
              <li>Expense type & classification</li>
              <li>Contribution type (if applicable)</li>
            </ul>
          </div>

          <div className="feature-card">
            <div className="feature-icon"><BarChart3 size={32} /></div>
            <h3>Report Metadata</h3>
            <ul>
              <li>Reporting period (start/end)</li>
              <li>Filing date</li>
              <li>Beginning & ending cash balance</li>
              <li>Total monetary receipts</li>
              <li>Total disbursements</li>
              <li>Committee information</li>
            </ul>
          </div>
        </div>
      </section>

      {/* How Calculations Work */}
      <section className="content-section gray-bg">
        <h2>Financial Reconciliation</h2>
        <p className="section-intro">
          We don't just extract data—we verify it. Every report is checked for accuracy using the fundamental cash flow equation:
        </p>

        <div className="calculation-box">
          <div className="formula">
            <span className="formula-item">Beginning Cash</span>
            <span className="operator">+</span>
            <span className="formula-item">Monetary Income</span>
            <span className="operator">−</span>
            <span className="formula-item">Cash Expenses</span>
            <span className="operator">=</span>
            <span className="formula-item highlight">Ending Cash</span>
          </div>
        </div>

        <div className="accuracy-stats">
          <div className="stat-card">
            <div className="stat-number">99.94%</div>
            <div className="stat-label">Financial Accuracy</div>
            <p className="stat-detail">Only $30 variance across $102,855 in transactions</p>
          </div>

          <div className="stat-card">
            <div className="stat-number">100%</div>
            <div className="stat-label">Internal Consistency</div>
            <p className="stat-detail">All reports balance perfectly within themselves</p>
          </div>

          <div className="stat-card">
            <div className="stat-number">&lt;1s</div>
            <div className="stat-label">Variance Detection</div>
            <p className="stat-detail">Automatically identifies gaps and discrepancies</p>
          </div>
        </div>
      </section>

      {/* Time Savings */}
      <section className="content-section">
        <h2>Massive Time Savings</h2>
        <p className="section-intro">
          Stop spending hours on manual data entry. Our platform processes reports in seconds.
        </p>

        <div className="comparison-table">
          <div className="comparison-row header">
            <div className="comparison-cell">Task</div>
            <div className="comparison-cell">Manual Entry</div>
            <div className="comparison-cell highlight-col">SHOWME</div>
            <div className="comparison-cell">Time Saved</div>
          </div>

          <div className="comparison-row">
            <div className="comparison-cell">Single Report (avg 30 transactions)</div>
            <div className="comparison-cell">15-30 minutes</div>
            <div className="comparison-cell highlight-col">~30 seconds</div>
            <div className="comparison-cell savings">14-29 minutes</div>
          </div>

          <div className="comparison-row">
            <div className="comparison-cell">10 Reports</div>
            <div className="comparison-cell">2.5-5 hours</div>
            <div className="comparison-cell highlight-col">~5 minutes</div>
            <div className="comparison-cell savings">2.5-5 hours</div>
          </div>

          <div className="comparison-row">
            <div className="comparison-cell">30 Reports (1 month)</div>
            <div className="comparison-cell">7.5-15 hours</div>
            <div className="comparison-cell highlight-col">~15 minutes</div>
            <div className="comparison-cell savings">7-15 hours</div>
          </div>

          <div className="comparison-row highlight-row">
            <div className="comparison-cell">Annual (360 reports)</div>
            <div className="comparison-cell">90-180 hours</div>
            <div className="comparison-cell highlight-col">~3 hours</div>
            <div className="comparison-cell savings">87-177 hours</div>
          </div>
        </div>

        <div className="time-callout">
          <strong>That's equivalent to 2-4 full work weeks saved per year!</strong>
        </div>
      </section>

      {/* Pricing Overview */}
      <section className="content-section gray-bg">
        <h2>Simple Annual Pricing</h2>
        <p className="section-intro">
          No credits, no metering, no math. Pick a plan and use it all year.
        </p>

        <div className="credit-breakdown">
          <div className="credit-card">
            <div className="credit-icon"><FileText size={32} /></div>
            <h3>Basic — $10/year</h3>
            <div className="credit-cost">Up to 100 uploads/yr</div>
            <p>Includes:</p>
            <ul>
              <li>Up to 100 report uploads per year</li>
              <li>Unlimited viewing of reports you upload</li>
              <li>Automated data extraction</li>
              <li>Download CSV/XLSX</li>
              <li>Email support</li>
            </ul>
          </div>

          <div className="credit-card">
            <div className="credit-icon"><Unlock size={32} /></div>
            <h3>Max — $20/year</h3>
            <div className="credit-cost">Unlimited</div>
            <p>Includes:</p>
            <ul>
              <li>Unlimited report uploads</li>
              <li>Access to every report in the system</li>
              <li>Full analytics dashboard</li>
              <li>Reporting Entity Groups</li>
              <li>Priority email support</li>
            </ul>
            <p className="credit-note">
              Duplicate uploads are free on every tier — we detect them automatically.
            </p>
          </div>
        </div>
      </section>

      {/* Infrastructure Costs */}
      <section className="content-section">
        <h2>Why We Charge</h2>
        <p className="section-intro">
          Building and maintaining a 99.94% accurate extraction system isn't free. Here's what your subscription supports:
        </p>

        <div className="cost-grid">
          <div className="cost-card">
            <h4 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Server size={20} style={{ flexShrink: 0 }} />
              <span>Infrastructure</span>
            </h4>
            <p>High-performance servers for PDF processing, OCR, and pattern matching</p>
          </div>

          <div className="cost-card">
            <h4 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Database size={20} style={{ flexShrink: 0 }} />
              <span>Database Storage</span>
            </h4>
            <p>Secure PostgreSQL database with automatic backups and 99.9% uptime</p>
          </div>

          <div className="cost-card">
            <h4 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <TrendingUp size={20} style={{ flexShrink: 0 }} />
              <span>Analytics Engine</span>
            </h4>
            <p>Real-time computation of trends, aggregations, and financial reconciliation</p>
          </div>

          <div className="cost-card">
            <h4 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Lock size={20} style={{ flexShrink: 0 }} />
              <span>Security</span>
            </h4>
            <p>Bank-level encryption, secure authentication, and HTTPS certificates</p>
          </div>

          <div className="cost-card">
            <h4 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Wrench size={20} style={{ flexShrink: 0 }} />
              <span>Development</span>
            </h4>
            <p>Continuous improvements, bug fixes, and new feature development</p>
          </div>

          <div className="cost-card">
            <h4 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <MessageCircle size={20} style={{ flexShrink: 0 }} />
              <span>Support</span>
            </h4>
            <p>Email support, documentation, and user assistance</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <h2>Ready to Save Time?</h2>
        <p>Sign up free for 3 trial uploads, or subscribe for as little as $10/year.</p>
        <div className="cta-buttons">
          <Link to="/signup" className="btn-primary large">
            Get Started Free
          </Link>
          <Link to="/account/pricing" className="btn-outline large">
            View Pricing
          </Link>
        </div>
      </section>
    </main>
  );
};
